import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { io, Socket } from 'socket.io-client';
import { API_BASE_URL, PRINTS_SOCKET_PATH } from 'src/config';
import { tokenSelector } from 'src/features/auth';
import { currentPosAgencySelector } from 'src/features/pos';
import { IdModel } from 'src/types';
import { DocumentPrintPayload } from '../printer.types';



type Props = (doc: DocumentPrintPayload) => void;

const SUBSCRIBED_TO_PRINT_REQUEST = 'subscribed_to_print_request';
const SUBSCRIBED_ME = 'subscribe_me';
const CONNECTED_FROM_AN_OTHER_TAB = 'connected_from_an_other_tab';
const DISCONNECT = 'disconnect';
const START_PRINT = 'start_print';
const TUNNEL_OPENED = 'tunnel_opened';
const CONNECT_ERROR = 'connect_error';

export const useSocketPrinter = (onPrintRequest: Props) => {
  const currentPosAgency = useSelector(currentPosAgencySelector);
  const token = useSelector(tokenSelector);
  const socketRef = useRef<Socket>();
  const [connectedToServer, setConnectedToServer] = useState<boolean>(false);
  const [socketError, setSocketError] = useState<Error | null>(null);

  useEffect(() => {
    if (token && currentPosAgency) {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      socketRef.current = io(API_BASE_URL, {
        path: PRINTS_SOCKET_PATH,
        auth: {
          token,
          agencyId: currentPosAgency,
        },
      });
    }
  }, [token, currentPosAgency]);

  const onSubscribedToPrintRequest = useCallback(
    (data) => {
      setConnectedToServer(true);
      setSocketError(null);
    },
    [setConnectedToServer, setSocketError],
  );

  const onDisconnect = useCallback(() => {
    socketRef.current?.disconnect();
    setConnectedToServer(false);
  }, [setConnectedToServer]);

  const onPrintWillStart = useCallback(
    (data: DocumentPrintPayload) => {
      onPrintRequest(data);
    },
    [onPrintRequest],
  );

  const onTunnelOpened = useCallback(() => {
    socketRef.current?.emit(SUBSCRIBED_ME, { currentPosAgency });
  }, [currentPosAgency, socketRef]);

  const onDisconnectError = useCallback(
    (error: Error) => {
      setSocketError(error);
      setConnectedToServer(false);
    },
    [setSocketError, setConnectedToServer],
  );

  const onConnectedFromOtherPlace = useCallback(() => {
    setSocketError(new Error('printer.connected_from_other_place_or_tab'));
    setConnectedToServer(false);
  }, [setSocketError, setConnectedToServer]);

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.removeListener(SUBSCRIBED_TO_PRINT_REQUEST, onSubscribedToPrintRequest);
      socketRef.current.removeListener(CONNECTED_FROM_AN_OTHER_TAB, onConnectedFromOtherPlace);
      socketRef.current.removeListener(DISCONNECT, onDisconnect);
      socketRef.current.removeListener(START_PRINT, onPrintWillStart);
      socketRef.current.removeListener(TUNNEL_OPENED, onTunnelOpened);
      socketRef.current.removeListener(CONNECT_ERROR, onDisconnectError);


      socketRef.current.on(SUBSCRIBED_TO_PRINT_REQUEST, onSubscribedToPrintRequest);
      socketRef.current.on(CONNECTED_FROM_AN_OTHER_TAB, onConnectedFromOtherPlace);
      socketRef.current.on(DISCONNECT, onDisconnect);
      socketRef.current.on(START_PRINT, onPrintWillStart);
      socketRef.current.on(TUNNEL_OPENED, onTunnelOpened);
      socketRef.current.on(CONNECT_ERROR, onDisconnectError);

    }

    return () => {
      if (socketRef.current) {
        socketRef.current.removeListener(SUBSCRIBED_TO_PRINT_REQUEST, onSubscribedToPrintRequest);
        socketRef.current.removeListener(CONNECTED_FROM_AN_OTHER_TAB, onConnectedFromOtherPlace);
        socketRef.current.removeListener(DISCONNECT, onDisconnect);
        socketRef.current.removeListener(START_PRINT, onPrintWillStart);
        socketRef.current.removeListener(TUNNEL_OPENED, onTunnelOpened);
        socketRef.current.removeListener(CONNECT_ERROR, onDisconnectError);
      }
    };
  }, [
    socketRef,
    onPrintWillStart,
    onTunnelOpened,
    onSubscribedToPrintRequest,
    onDisconnectError,
    onDisconnect,
    onConnectedFromOtherPlace,
  ]);

  const subscribeToPrinterFlux = useCallback(() => {
    if (!currentPosAgency) {
      return setSocketError(new Error('pos.required-pos-agency'));
    }

    if (!token) {
      return setSocketError(new Error('pos.required-authentication'));
    }
  }, [token, setSocketError, currentPosAgency]);

  useEffect(() => {
    subscribeToPrinterFlux();
  }, [subscribeToPrinterFlux, token, currentPosAgency]);

  return {
    socketError,
    connectedToServer,
  };
};
